/**
 * The attributes of one tree level, from the level's own panel.
 *
 * The attribute-setup screen works from the set outward: pick a set, then say
 * where it applies. This is the other way round, for the person standing on a
 * segment: *what do the articles here carry?* They see the set the level has —
 * its own, or the one it inherits — add an attribute to it, or make a new set
 * and attach it in one confirmation.
 *
 * Nothing here touches an article. An article's group is resolved from its
 * category (`resolve_attribute_group`), so attaching the set to the segment is
 * all it takes for every article placed there to carry it: the inputs appear
 * on the article page, and the required ones gate publication.
 */

import { useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@hardauto/core-api";
import { useAction } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import { Badge, Button, Checkbox, Dialog, Input, Select, useToast } from "@hardauto/core-ui";

import type {
  AttributeBody,
  AttributeGroup,
  AttributeType,
  CatalogApi,
  CategoryNode,
} from "../../api";
import { NAMESPACE } from "../../i18n";
import { flatten, inheritedGroup } from "./tree";

const TYPES: readonly AttributeType[] = ["text", "number", "choice", "boolean"];

interface Draft {
  label: string;
  type: AttributeType;
  options: string;
  required: boolean;
}

const EMPTY_DRAFT: Draft = { label: "", type: "text", options: "", required: false };

/**
 * This section sits inside the panel's own `<form>`, so Enter in one of its
 * inputs would submit *that* form. It must do nothing here: these inputs are
 * confirmed by their own buttons.
 */
function ignoreEnter(event: KeyboardEvent<HTMLInputElement>) {
  if (event.key === "Enter") event.preventDefault();
}

/** The API body for a draft, or why it cannot be sent yet. */
function bodyOf(draft: Draft): AttributeBody | "label" | "options" {
  const label = draft.label.trim();
  if (!label) return "label";
  const options = draft.options
    .split(/[\n,;]/)
    .map((item) => item.trim())
    .filter(Boolean);
  if (draft.type === "choice" && options.length === 0) return "options";
  return {
    label,
    type: draft.type,
    required: draft.required,
    ...(draft.type === "choice" ? { options } : {}),
  };
}

export interface AttributeSetSectionProps {
  api: CatalogApi;
  node: CategoryNode;
  roots: CategoryNode[];
  groups: AttributeGroup[];
  mayWrite: boolean;
  /** The tree changed (a set was attached): read it again. */
  onChanged: () => void;
  /** A set or one of its attributes changed: read the sets again. */
  onGroupsChanged: () => void;
}

export function AttributeSetSection(props: AttributeSetSectionProps) {
  const { api, node, roots, groups, mayWrite } = props;
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();
  const { category } = node;

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [rows, setRows] = useState<Draft[]>([EMPTY_DRAFT]);
  const [extra, setExtra] = useState<Draft>(EMPTY_DRAFT);

  // What the level has today: its own set, or the one it gets from above.
  const ownId = category.attribute_group_id ?? "";
  const above = inheritedGroup(roots, category.id);
  // What the select shows. Choosing is not saving: `Apply` is.
  const [choice, setChoice] = useState(ownId);
  const changed = choice !== ownId;
  const shownId = choice || above?.groupId || "";
  const group = shownId ? groups.find((item) => item.id === shownId) : undefined;
  const sharedWith = choice
    ? flatten(roots).filter(
        ({ node: other }) =>
          other.category.attribute_group_id === choice && other.category.id !== category.id,
      ).length
    : 0;

  function failed(error: unknown) {
    toast.show(error instanceof ApiError ? error.message : t("error.title"), { tone: "danger" });
  }

  function reset() {
    setCreating(false);
    setName("");
    setRows([EMPTY_DRAFT]);
  }

  function patchRow(index: number, patch: Partial<Draft>) {
    setRows((current) => current.map((row, at) => (at === index ? { ...row, ...patch } : row)));
  }

  const filled = rows.filter((row) => row.label.trim() || row.options.trim());
  const bodies = filled.map(bodyOf);
  const invalid = bodies.some((body) => typeof body === "string");
  const canCreate = name.trim() !== "" && filled.length > 0 && !invalid;

  return (
    <section className="hardauto-catpanel__section" aria-labelledby={`attrs-${category.id}`}>
      <h3 id={`attrs-${category.id}`} className="hardauto-catpanel__subtitle">
        {t("panel.attrs.title")}
      </h3>

      {mayWrite ? (
        <div className="hardauto-catpanel__attr-pick">
          <Field
            label={t("panel.attrs.pick")}
            hint={groups.length === 0 ? t("panel.attrs.noSets") : t("panel.attrs.pickHint")}
          >
            {(fieldProps) => (
              <Select
                {...fieldProps}
                value={choice}
                disabled={groups.length === 0}
                onChange={(event) => setChoice(event.target.value)}
              >
                <option value="">{t("categories.groupInherit")}</option>
                {groups.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Button
            size="sm"
            variant="primary"
            disabled={!changed}
            busy={action.isBusy("apply")}
            onClick={() => {
              void action.run("apply", async () => {
                try {
                  await api.updateCategory(
                    category.id,
                    choice ? { attribute_group_id: choice } : { clear_attribute_group: true },
                  );
                  toast.show(t("panel.attrs.applied", { level: category.label }), {
                    tone: "success",
                  });
                  props.onChanged();
                } catch (error) {
                  failed(error);
                }
              });
            }}
          >
            {t("panel.attrs.apply", { level: category.label })}
          </Button>
        </div>
      ) : null}

      {group ? (
        <>
          <p className="hardauto-cell__muted">
            {changed
              ? t("panel.attrs.preview", { group: group.label })
              : choice
                ? t("panel.attrs.own", { group: group.label })
                : above
                  ? t("panel.attrs.inherited", { group: group.label, from: above.from })
                  : ""}
          </p>
          {sharedWith > 0 ? (
            <p className="hardauto-cell__muted">{t("panel.attrs.shared", { count: sharedWith })}</p>
          ) : null}
          {group.attributes.length === 0 ? (
            <p className="hardauto-cell__muted">{t("panel.attrs.empty")}</p>
          ) : (
            <ul className="hardauto-catpanel__attrs">
              {group.attributes.map((item) => (
                <li key={item.id}>
                  <span>{item.label}</span>
                  <span className="hardauto-cell__muted">{t(`attributes.type.${item.type}`)}</span>
                  {item.required ? (
                    <Badge tone="warning">{t("attributes.column.required")}</Badge>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <p className="hardauto-cell__muted">{t("panel.attrs.none")}</p>
      )}

      {mayWrite && group && !changed ? (
        <div className="hardauto-catpanel__attr-add">
          <AttributeFields draft={extra} onChange={(patch) => setExtra({ ...extra, ...patch })} />
          <Button
            size="sm"
            disabled={typeof bodyOf(extra) === "string"}
            busy={action.isBusy("add")}
            onClick={() => {
              const body = bodyOf(extra);
              if (typeof body === "string") return;
              void action.run("add", async () => {
                try {
                  await api.addAttribute(group.id, body);
                  setExtra(EMPTY_DRAFT);
                  toast.show(t("panel.attrs.added"), { tone: "success" });
                  props.onGroupsChanged();
                } catch (error) {
                  failed(error);
                }
              });
            }}
          >
            {t("panel.attrs.add")}
          </Button>
        </div>
      ) : null}

      {mayWrite ? (
        <Button size="sm" onClick={() => setCreating(true)}>
          {t("panel.attrs.newSet")}
        </Button>
      ) : null}

      <Dialog
        open={creating}
        onClose={reset}
        title={t("panel.attrs.newTitle", { level: category.label })}
        description={t("panel.attrs.newDescription")}
        closeLabel={t("panel.attrs.close")}
        footer={
          <>
            <Button variant="ghost" onClick={reset}>
              {t("panel.attrs.cancel")}
            </Button>
            <Button
              variant="primary"
              disabled={!canCreate}
              busy={action.isBusy("create")}
              busyLabel={t("panel.attrs.creating")}
              onClick={() => {
                void action.run("create", async () => {
                  try {
                    const created = await api.createAttributeGroup({ label: name.trim() });
                    for (const body of bodies) {
                      if (typeof body !== "string") await api.addAttribute(created.id, body);
                    }
                    await api.updateCategory(category.id, { attribute_group_id: created.id });
                    toast.show(t("panel.attrs.created", { level: category.label }), {
                      tone: "success",
                    });
                    reset();
                    props.onChanged();
                    props.onGroupsChanged();
                  } catch (error) {
                    failed(error);
                    // The set may exist already; show it rather than hide it.
                    props.onGroupsChanged();
                  }
                });
              }}
            >
              {t("panel.attrs.create", { level: category.label })}
            </Button>
          </>
        }
      >
        <div className="hardauto-stack">
          <Field label={t("panel.attrs.setName")} required>
            {(fieldProps) => (
              <Input
                {...fieldProps}
                value={name}
                onKeyDown={ignoreEnter}
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </Field>
          {rows.map((row, index) => (
            <div key={index} className="hardauto-catpanel__attr-row">
              <AttributeFields draft={row} onChange={(patch) => patchRow(index, patch)} />
              {rows.length > 1 ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setRows((current) => current.filter((_, at) => at !== index))}
                >
                  {t("panel.attrs.removeRow")}
                </Button>
              ) : null}
            </div>
          ))}
          <div>
            <Button size="sm" onClick={() => setRows((current) => [...current, EMPTY_DRAFT])}>
              {t("panel.attrs.addRow")}
            </Button>
          </div>
        </div>
      </Dialog>
    </section>
  );
}

/** One attribute's inputs: label, type, the choices of a choice, required. */
function AttributeFields({
  draft,
  onChange,
}: {
  draft: Draft;
  onChange: (patch: Partial<Draft>) => void;
}) {
  const { t } = useTranslation(NAMESPACE);
  return (
    <div className="hardauto-catpanel__attr-fields">
      <Field label={t("attributes.column.label")}>
        {(fieldProps) => (
          <Input
            {...fieldProps}
            value={draft.label}
            onKeyDown={ignoreEnter}
            onChange={(event) => onChange({ label: event.target.value })}
          />
        )}
      </Field>
      <Field label={t("attributes.column.type")}>
        {(fieldProps) => (
          <Select
            {...fieldProps}
            value={draft.type}
            onChange={(event) => onChange({ type: event.target.value as AttributeType })}
          >
            {TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`attributes.type.${type}`)}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {draft.type === "choice" ? (
        <Field label={t("panel.attrs.options")} hint={t("panel.attrs.optionsHint")}>
          {(fieldProps) => (
            <Input
              {...fieldProps}
              value={draft.options}
              onKeyDown={ignoreEnter}
              onChange={(event) => onChange({ options: event.target.value })}
            />
          )}
        </Field>
      ) : null}
      <Checkbox
        label={t("attributes.column.required")}
        checked={draft.required}
        onChange={(event) => onChange({ required: event.target.checked })}
      />
    </div>
  );
}
